import React, { useEffect, useRef, useState } from 'react';
import './StatusArrowWidget.css';
import {
  ARROW_ANCHOR_OPTIONS,
  STATUS_ARROW_WIDGET_TYPE,
  resolveStatusVisualState,
} from './assetStatusUtils';

const isActionArea = (target) => {
  if (!target || !(target instanceof Element)) return false;
  return Boolean(target.closest('button,a,input,select,textarea,[role="button"],[data-no-drag],.widget-buttons-container'));
};

const buildDragData = (event, id) => {
  const widgetWrapper = event.target.closest('.widget');
  const widgetId = id || (widgetWrapper ? widgetWrapper.id : '');
  const rect = event.currentTarget.getBoundingClientRect();
  const offsetX = event.clientX - rect.left;
  const offsetY = event.clientY - rect.top;
  return `${STATUS_ARROW_WIDGET_TYPE};${widgetId};${offsetX};${offsetY};${widgetId.startsWith('base') ? 'add' : 'move'}`;
};

const anchorPoint = (anchor, width, height, padding) => {
  const xMin = padding;
  const xMax = Math.max(padding, width - padding);
  const yMin = padding;
  const yMax = Math.max(padding, height - padding);
  const yMid = height / 2;
  const xMid = width / 2;

  switch (anchor) {
    case 'left':
      return { x: xMin, y: yMid };
    case 'top':
      return { x: xMid, y: yMin };
    case 'bottom':
      return { x: xMid, y: yMax };
    case 'right':
    default:
      return { x: xMax, y: yMid };
  }
};

const roundedCornerPath = (points, radius = 14) => {
  const validPoints = points.filter((point) => point && Number.isFinite(point.x) && Number.isFinite(point.y));
  if (validPoints.length < 2) return '';
  if (validPoints.length === 2) {
    return `M ${validPoints[0].x} ${validPoints[0].y} L ${validPoints[1].x} ${validPoints[1].y}`;
  }

  const parts = [`M ${validPoints[0].x} ${validPoints[0].y}`];

  for (let index = 1; index < validPoints.length - 1; index += 1) {
    const previous = validPoints[index - 1];
    const current = validPoints[index];
    const next = validPoints[index + 1];
    const prevDistance = Math.hypot(current.x - previous.x, current.y - previous.y);
    const nextDistance = Math.hypot(next.x - current.x, next.y - current.y);
    const cornerRadius = Math.min(radius, prevDistance / 2, nextDistance / 2);

    if (cornerRadius <= 0.5) {
      parts.push(`L ${current.x} ${current.y}`);
      continue;
    }

    const before = {
      x: current.x - ((current.x - previous.x) / prevDistance) * cornerRadius,
      y: current.y - ((current.y - previous.y) / prevDistance) * cornerRadius,
    };
    const after = {
      x: current.x + ((next.x - current.x) / nextDistance) * cornerRadius,
      y: current.y + ((next.y - current.y) / nextDistance) * cornerRadius,
    };

    parts.push(`L ${before.x} ${before.y}`);
    parts.push(`Q ${current.x} ${current.y} ${after.x} ${after.y}`);
  }

  const last = validPoints[validPoints.length - 1];
  parts.push(`L ${last.x} ${last.y}`);
  return parts.join(' ');
};

const pathForShape = ({ shape, start, end, width, height, bend = 50, padding = 14 }) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const bendRatio = Math.max(0, Math.min(100, Number(bend) || 50)) / 100;
  const midX = start.x + dx * bendRatio;
  const midY = start.y + dy * bendRatio;
  const distance = Math.max(1, Math.hypot(dx, dy));
  const normal = {
    x: -dy / distance,
    y: dx / distance,
  };
  const minDimension = Math.max(1, Math.min(width, height));
  const bendStrength = 0.08 + bendRatio * 0.52;
  const curveOffset = Math.max(8, minDimension * bendStrength);
  const stepOffset = Math.max(10, minDimension * (0.12 + bendRatio * 0.5));
  const elbowOffset = Math.max(10, minDimension * (0.12 + bendRatio * 0.36));
  const hasVisibleVerticalChange = Math.abs(dy) > 8;
  const hasVisibleHorizontalChange = Math.abs(dx) > 8;
  const fallbackY = start.y + (start.y <= height / 2 ? elbowOffset : -elbowOffset);
  const fallbackX = start.x + (start.x <= width / 2 ? elbowOffset : -elbowOffset);
  const elbowMidY = hasVisibleVerticalChange ? midY : Math.max(padding, Math.min(height - padding, fallbackY));
  const elbowMidX = hasVisibleHorizontalChange ? midX : Math.max(padding, Math.min(width - padding, fallbackX));

  if (shape === 'curve') {
    const c1 = {
      x: start.x + dx * 0.34 + normal.x * curveOffset,
      y: start.y + dy * 0.34 + normal.y * curveOffset,
    };
    const c2 = {
      x: start.x + dx * 0.66 + normal.x * curveOffset,
      y: start.y + dy * 0.66 + normal.y * curveOffset,
    };
    return `M ${start.x} ${start.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${end.x} ${end.y}`;
  }

  if (shape === 's-curve') {
    const center = {
      x: start.x + dx * 0.5,
      y: start.y + dy * 0.5,
    };
    const c1 = {
      x: start.x + dx * 0.22 + normal.x * curveOffset,
      y: start.y + dy * 0.22 + normal.y * curveOffset,
    };
    const c2 = {
      x: start.x + dx * 0.34 + normal.x * curveOffset,
      y: start.y + dy * 0.34 + normal.y * curveOffset,
    };
    const c3 = {
      x: start.x + dx * 0.66 - normal.x * curveOffset,
      y: start.y + dy * 0.66 - normal.y * curveOffset,
    };
    const c4 = {
      x: start.x + dx * 0.78 - normal.x * curveOffset,
      y: start.y + dy * 0.78 - normal.y * curveOffset,
    };
    return `M ${start.x} ${start.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${center.x} ${center.y} C ${c3.x} ${c3.y}, ${c4.x} ${c4.y}, ${end.x} ${end.y}`;
  }

  if (shape === 'step') {
    return roundedCornerPath([
      start,
      { x: start.x + dx * 0.28, y: start.y },
      {
        x: start.x + dx * 0.28 + normal.x * stepOffset,
        y: start.y + dy * 0.5 + normal.y * stepOffset,
      },
      {
        x: start.x + dx * 0.72 + normal.x * stepOffset,
        y: start.y + dy * 0.5 + normal.y * stepOffset,
      },
      { x: start.x + dx * 0.72, y: end.y },
      end,
    ], 12);
  }

  if (shape === 'elbow-2') {
    if (!hasVisibleVerticalChange) {
      return roundedCornerPath([
        start,
        { x: start.x, y: elbowMidY },
        { x: end.x, y: elbowMidY },
        end,
      ], 14);
    }

    if (!hasVisibleHorizontalChange) {
      return roundedCornerPath([
        start,
        { x: elbowMidX, y: start.y },
        { x: elbowMidX, y: end.y },
        end,
      ], 14);
    }

    return roundedCornerPath([
      start,
      { x: start.x, y: elbowMidY },
      { x: end.x, y: elbowMidY },
      end,
    ], 14);
  }

  if (shape === 'elbow') {
    if (!hasVisibleVerticalChange) {
      return roundedCornerPath([
        start,
        { x: midX, y: start.y },
        { x: midX, y: elbowMidY },
        { x: end.x, y: elbowMidY },
        end,
      ], 14);
    }

    if (!hasVisibleHorizontalChange) {
      return roundedCornerPath([
        start,
        { x: start.x, y: midY },
        { x: elbowMidX, y: midY },
        { x: elbowMidX, y: end.y },
        end,
      ], 14);
    }

    return roundedCornerPath([
      start,
      { x: elbowMidX, y: start.y },
      { x: elbowMidX, y: end.y },
      end,
    ], 14);
  }

  return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
};

const StatusArrowWidget = ({
  id,
  draggable,
  dims,
  shape = 'elbow',
  startAnchor = 'left',
  endAnchor = 'right',
  color = '#26b4ff',
  strokeWidth = 3,
  dash = 'none',
  bend = 50,
  showStartDot = true,
  showEndArrow = true,
  reverse = false,
  animated = false,
  startPoint,
  endPoint,
  value,
  changeColorBasedOnValue = false,
  colorGrading = false,
  colors = [],
  statusRules = [],
  onWidgetPropsChange,
}) => {
  const [cursorState, setCursorState] = useState('idle');
  const [draftPoints, setDraftPoints] = useState(null);
  const [pointDragActive, setPointDragActive] = useState(false);
  const containerRef = useRef(null);
  const width = Math.max(40, Number(dims?.width) || 220);
  const height = Math.max(28, Number(dims?.height) || 90);
  const stroke = Math.max(1, Math.min(18, Number(strokeWidth) || 3));
  const padding = Math.max(14, stroke * 3);
  const safeStartAnchor = ARROW_ANCHOR_OPTIONS.some((option) => option.value === startAnchor) ? startAnchor : 'left';
  const safeEndAnchor = ARROW_ANCHOR_OPTIONS.some((option) => option.value === endAnchor) ? endAnchor : 'right';
  const normalizePoint = (point) => {
    const x = Number(point?.x);
    const y = Number(point?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return {
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    };
  };

  const pointToPixels = (point) => ({
    x: (point.x / 100) * width,
    y: (point.y / 100) * height,
  });

  const pointFromPixels = (x, y) => ({
    x: Math.max(0, Math.min(100, (x / Math.max(1, width)) * 100)),
    y: Math.max(0, Math.min(100, (y / Math.max(1, height)) * 100)),
  });

  const normalizedStartPoint = normalizePoint(draftPoints?.startPoint || startPoint);
  const normalizedEndPoint = normalizePoint(draftPoints?.endPoint || endPoint);
  const anchoredStart = anchorPoint(safeStartAnchor, width, height, padding);
  const anchoredEnd = anchorPoint(safeEndAnchor, width, height, padding);
  const rawStart = normalizedStartPoint ? pointToPixels(normalizedStartPoint) : anchoredStart;
  const rawEnd = normalizedEndPoint ? pointToPixels(normalizedEndPoint) : anchoredEnd;
  const start = reverse ? rawEnd : rawStart;
  const end = reverse ? rawStart : rawEnd;

  useEffect(() => {
    setDraftPoints(null);
  }, [startPoint?.x, startPoint?.y, endPoint?.x, endPoint?.y]);

  const state = resolveStatusVisualState({
    value,
    baseColor: color,
    rules: statusRules,
    colors,
    changeColorBasedOnValue,
    colorGrading,
  });

  const markerId = `status-arrow-head-${String(id || 'preview').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const glowId = `status-arrow-glow-${String(id || 'preview').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const path = pathForShape({ shape, start, end, width, height, bend, padding });
  const dashArray = dash === 'dashed' ? '12 10' : dash === 'dotted' ? '2 9' : 'none';
  const canEditPoints = draggable && typeof onWidgetPropsChange === 'function';

  const beginPointDrag = (event, pointKey) => {
    if (!canEditPoints) return;
    event.preventDefault();
    event.stopPropagation();
    setPointDragActive(true);

    const target = event.currentTarget;
    if (target?.setPointerCapture && event.pointerId !== undefined) {
      try {
        target.setPointerCapture(event.pointerId);
      } catch (_error) {
      }
    }

    const initialStart = normalizePoint(draftPoints?.startPoint || startPoint)
      || pointFromPixels(anchoredStart.x, anchoredStart.y);
    const initialEnd = normalizePoint(draftPoints?.endPoint || endPoint)
      || pointFromPixels(anchoredEnd.x, anchoredEnd.y);

    const move = (moveEvent) => {
      moveEvent.preventDefault();
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const nextPoint = pointFromPixels(
        moveEvent.clientX - rect.left,
        moveEvent.clientY - rect.top,
      );
      setDraftPoints({
        startPoint: pointKey === 'startPoint' ? nextPoint : initialStart,
        endPoint: pointKey === 'endPoint' ? nextPoint : initialEnd,
      });
    };

    const up = (upEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      const nextPoint = rect
        ? pointFromPixels(upEvent.clientX - rect.left, upEvent.clientY - rect.top)
        : null;
      const committed = {
        startPoint: pointKey === 'startPoint' && nextPoint ? nextPoint : initialStart,
        endPoint: pointKey === 'endPoint' && nextPoint ? nextPoint : initialEnd,
      };
      setDraftPoints(committed);
      onWidgetPropsChange(committed);
      setPointDragActive(false);
      document.documentElement.removeEventListener('pointermove', move);
      document.documentElement.removeEventListener('pointerup', up);
      document.documentElement.removeEventListener('pointercancel', cancel);
    };

    const cancel = () => {
      setPointDragActive(false);
      document.documentElement.removeEventListener('pointermove', move);
      document.documentElement.removeEventListener('pointerup', up);
      document.documentElement.removeEventListener('pointercancel', cancel);
    };

    document.documentElement.addEventListener('pointermove', move);
    document.documentElement.addEventListener('pointerup', up);
    document.documentElement.addEventListener('pointercancel', cancel);
  };

  const visualHandles = {
    start: reverse ? end : start,
    end: reverse ? start : end,
  };

  return (
    <div
      ref={containerRef}
      className={[
        'status-arrow-widget',
        draggable ? (cursorState === 'grabbing' ? 'status-arrow-widget--grabbing' : 'status-arrow-widget--grab') : '',
        animated ? 'status-arrow-widget--animated' : '',
      ].filter(Boolean).join(' ')}
      draggable={draggable && !pointDragActive}
      style={{ '--status-arrow-color': state.color }}
      onMouseDown={(event) => {
        if (!draggable || isActionArea(event.target)) return;
        setCursorState('grabbing');
      }}
      onMouseUp={(event) => {
        if (!draggable || isActionArea(event.target)) return;
        setCursorState('grab');
      }}
      onMouseLeave={() => draggable && setCursorState('grab')}
      onDragStart={(event) => {
        if (isActionArea(event.target)) {
          event.preventDefault();
          return;
        }
        event.dataTransfer.setData('text/plain', buildDragData(event, id));
      }}
    >
      <svg className="status-arrow-widget__svg" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <defs>
          <filter id={glowId} x="-25%" y="-25%" width="150%" height="150%">
            <feGaussianBlur stdDeviation="2.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <marker
            id={markerId}
            markerWidth="10"
            markerHeight="10"
            refX="8"
            refY="5"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={state.color} />
          </marker>
        </defs>
        <path
          className="status-arrow-widget__glow"
          d={path}
          fill="none"
          stroke={state.color}
          strokeWidth={stroke + 5}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.2"
          filter={`url(#${glowId})`}
        />
        <path
          className="status-arrow-widget__line"
          d={path}
          fill="none"
          stroke={state.color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={dashArray}
          markerEnd={showEndArrow ? `url(#${markerId})` : undefined}
        />
        {showStartDot ? (
          <circle
            className="status-arrow-widget__dot"
            cx={start.x}
            cy={start.y}
            r={Math.max(3, stroke * 1.4)}
            fill={state.color}
          />
        ) : null}
      </svg>
      {canEditPoints ? (
        <>
          <button
            type="button"
            className="status-arrow-widget__handle status-arrow-widget__handle--start"
            aria-label="Move arrow start point"
            title="Move arrow start point"
            data-no-drag="true"
            draggable={false}
            style={{
              left: `${visualHandles.start.x}px`,
              top: `${visualHandles.start.y}px`,
              '--status-arrow-handle-color': state.color,
            }}
            onPointerDown={(event) => beginPointDrag(event, reverse ? 'endPoint' : 'startPoint')}
            onDragStart={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          />
          <button
            type="button"
            className="status-arrow-widget__handle status-arrow-widget__handle--end"
            aria-label="Move arrow end point"
            title="Move arrow end point"
            data-no-drag="true"
            draggable={false}
            style={{
              left: `${visualHandles.end.x}px`,
              top: `${visualHandles.end.y}px`,
              '--status-arrow-handle-color': state.color,
            }}
            onPointerDown={(event) => beginPointDrag(event, reverse ? 'startPoint' : 'endPoint')}
            onDragStart={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          />
        </>
      ) : null}
    </div>
  );
};

export default StatusArrowWidget;
